import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Lock, ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { CharacterStage } from "./Characters";
import { apiError, fetchAuthStatus, getGateToken, login, openGate } from "../../lib/auth";

const PIN_LENGTH = Number(process.env.REACT_APP_GATE_PIN_LENGTH || 6);

const ErrorNote = ({ children }) =>
  children ? (
    <div
      data-testid="auth-error"
      className="p-3 text-sm text-rose-300 bg-rose-950/30 border border-rose-900/40 rounded-lg"
    >
      {children}
    </div>
  ) : null;

/* ------------------------------ Stage 1: the gate ------------------------------ */
const GatePin = ({ onOpen, onActivity, secondFactor = false }) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submitted = useRef("");

  const submit = async (value) => {
    if (busy || submitted.current === value) return;
    submitted.current = value;
    setBusy(true);
    setError("");
    try {
      await openGate(value);
      onOpen();
    } catch (err) {
      setError(apiError(err, "Incorrect PIN."));
      setPin("");
      submitted.current = "";
    } finally {
      setBusy(false);
    }
  };

  const change = (value) => {
    setPin(value);
    setError("");
    onActivity(value.length);
    if (value.length === PIN_LENGTH) submit(value);
  };

  return (
    <motion.div
      key="gate"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25 }}
      className="w-full max-w-[420px]"
      data-testid="gate-screen"
    >
      <div className="flex flex-col items-center text-center mb-9">
        <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center mb-5">
          <ShieldCheck className="w-6 h-6 text-indigo-300" />
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-50 mb-2">Access PIN</h1>
        <p className="text-sm text-slate-400 max-w-[300px]">
          {secondFactor
            ? `Super admin sign-in needs the ${PIN_LENGTH}-digit access PIN as well.`
            : `AutoCruise is in private mode. Enter the ${PIN_LENGTH}-digit PIN to reach the sign-in page.`}
        </p>
      </div>

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (pin.length === PIN_LENGTH) submit(pin);
        }}
      >
        <div className="flex justify-center">
          <InputOTP
            maxLength={PIN_LENGTH}
            value={pin}
            onChange={change}
            disabled={busy}
            autoFocus
            data-testid="gate-pin-input"
          >
            <InputOTPGroup className="gap-2.5">
              {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                <InputOTPSlot
                  key={i}
                  index={i}
                  className="h-14 w-12 rounded-xl border border-slate-700/80 bg-slate-900/60 text-xl font-semibold text-slate-100 first:rounded-xl last:rounded-xl"
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>

        <ErrorNote>{error}</ErrorNote>

        <Button
          type="submit"
          disabled={busy || pin.length !== PIN_LENGTH}
          className="w-full h-12 text-base font-medium bg-indigo-500 hover:bg-indigo-400 text-white"
          data-testid="gate-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlock"}
        </Button>
      </form>

      <p className="text-center text-xs text-slate-600 mt-8">
        Private deployment &middot; no public sign-ups
      </p>
    </motion.div>
  );
};

/* ------------------------------ Stage 2: sign in ------------------------------ */
const LoginForm = ({ onAuthed, onBack, onActivity, onNeedPin, gatePassed, registrationOpen }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    onActivity({ secretLength: password.length, secretVisible: showPassword });
  }, [password, showPassword, onActivity]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(username, password, remember);
      onAuthed();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 428) {
        // A super admin account: the PIN is its second factor. Hand the
        // credentials up so the login completes on its own once it is in.
        onNeedPin({ username, password, remember });
      } else if (status === 403 && apiError(err, "").includes("PIN")) {
        setError("Your PIN session expired. Enter the access PIN again.");
        setTimeout(onBack, 1200);
      } else {
        setError(apiError(err, "Invalid email or password."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      key="login"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25 }}
      className="w-full max-w-[420px]"
      data-testid="login-screen"
    >
      <div className="text-center mb-9">
        {gatePassed && (
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1 mb-5">
            <Lock className="w-3 h-3" /> Gateway unlocked
          </div>
        )}
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-50 mb-2">Welcome back</h1>
        <p className="text-sm text-slate-400">Sign in to your command center</p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="username" className="text-sm font-medium text-slate-300">
            Email
          </Label>
          <Input
            id="username"
            type="text"
            autoComplete="username"
            placeholder="you@company.com"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onFocus={() => onActivity({ typing: true })}
            onBlur={() => onActivity({ typing: false })}
            required
            data-testid="login-username"
            className="h-12 bg-slate-900/60 border-slate-700/80 text-slate-100 placeholder:text-slate-600 focus:border-indigo-500"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-slate-300">
            Password
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => onActivity({ typing: true })}
              onBlur={() => onActivity({ typing: false })}
              required
              data-testid="login-password"
              className="h-12 pr-10 bg-slate-900/60 border-slate-700/80 text-slate-100 placeholder:text-slate-600 focus:border-indigo-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="remember"
            checked={remember}
            onCheckedChange={(v) => setRemember(Boolean(v))}
            data-testid="login-remember"
          />
          <Label htmlFor="remember" className="text-sm font-normal text-slate-400 cursor-pointer">
            Keep me signed in for 30 days
          </Label>
        </div>

        <ErrorNote>{error}</ErrorNote>

        <Button
          type="submit"
          disabled={busy}
          className="w-full h-12 text-base font-medium bg-indigo-500 hover:bg-indigo-400 text-white"
          data-testid="login-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
        </Button>
      </form>

      {registrationOpen && (
        <p className="mt-6 text-center text-sm text-slate-400">
          New to AutoCruise?{" "}
          <Link to="/signup" className="font-semibold text-indigo-300 hover:text-indigo-200">
            Create an account
          </Link>
        </p>
      )}

      <div className="mt-8 flex items-center justify-center gap-5">
        {gatePassed && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to the gate
          </button>
        )}
        <Link to="/" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
          Back to the site
        </Link>
      </div>
    </motion.div>
  );
};

/* ------------------------------ Shell ------------------------------ */
export const AuthScreen = ({ onAuthed }) => {
  const [stage, setStage] = useState(null);           // null until the platform answers
  const [authStatus, setAuthStatus] = useState(null);
  const [pending, setPending] = useState(null);       // credentials waiting on the PIN
  const [mood, setMood] = useState({ typing: false, secretLength: 0, secretVisible: false });

  useEffect(() => {
    fetchAuthStatus()
      .then((st) => {
        setAuthStatus(st);
        // In maintenance the PIN comes first, as it always has. Once open,
        // customers go straight to the form and only a super admin is asked.
        setStage(st.gate_required && !getGateToken() ? "gate" : "login");
      })
      .catch(() => setStage("gate"));
  }, []);

  const afterGate = async () => {
    setMood({ typing: false, secretLength: 0, secretVisible: false });
    if (pending) {
      try {
        await login(pending.username, pending.password, pending.remember);
        onAuthed();
        return;
      } catch (err) {
        setPending(null);
      }
    }
    setStage("login");
  };

  /* Stable across renders: LoginForm calls this from an effect, so an
     identity that changed every render would loop. Returning the previous
     object when nothing actually changed keeps that effect quiet. */
  const nudge = useCallback((patch) => {
    setMood((m) => {
      const next = { ...m, ...patch };
      if (
        next.typing === m.typing &&
        next.secretLength === m.secretLength &&
        next.secretVisible === m.secretVisible
      ) {
        return m;
      }
      return next;
    });
  }, []);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ac-bg">
      {/* Left: the cast */}
      <div className="relative hidden lg:flex items-end justify-center overflow-hidden p-12 bg-ac-surface border-r border-ac-border">

        <div className="relative z-20 flex items-end justify-center">
          <CharacterStage
            typing={mood.typing}
            secretLength={mood.secretLength}
            secretVisible={mood.secretVisible}
          />
        </div>
      </div>

      {/* Right: the forms */}
      <div className="relative z-10 flex items-center justify-center p-8">
        <div className="w-full flex flex-col items-center">
          <AnimatePresence mode="wait">
            {stage === null ? (
              <Loader2 key="wait" className="w-6 h-6 text-indigo-400 animate-spin" />
            ) : stage === "gate" ? (
              <GatePin
                secondFactor={Boolean(pending)}
                onOpen={afterGate}
                onActivity={(len) => nudge({ typing: len > 0, secretLength: len, secretVisible: false })}
              />
            ) : (
              <LoginForm
                onAuthed={onAuthed}
                gatePassed={Boolean(getGateToken())}
                registrationOpen={Boolean(authStatus?.registration_open)}
                onNeedPin={(creds) => {
                  setPending(creds);
                  setStage("gate");
                }}
                onBack={() => {
                  setMood({ typing: false, secretLength: 0, secretVisible: false });
                  setStage("gate");
                }}
                onActivity={nudge}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
